import type { FC, ReactNode } from 'react'
import { createContext, useState, useMemo, useContext, useEffect } from 'react'

import type { Locale, WhiteLabel } from '@skytech/types'
import { DEFAULT_LOCALE, getWhiteLabelFromParams, isLocale } from '@skytech/utils'
import { useLocation } from 'react-router-dom'

type AppContext = {
  locale: Locale
  whiteLabel: WhiteLabel
  isEditTranslationsMode: boolean
  setLocale: (v: Locale) => void
  setWhiteLabel: (v: WhiteLabel) => void
  setIsEditTranslationsMode: (v: boolean) => void
}

const Context = createContext<AppContext>({
  locale: DEFAULT_LOCALE,
  whiteLabel: 'skytech',
  isEditTranslationsMode: false,
  setLocale: () => {},
  setWhiteLabel: () => {},
  setIsEditTranslationsMode: () => {},
})

export const AppContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation()

  // todo: adjust to the other apps standard
  let browserLocale = navigator.language.split(/[-_]/)[0]
  // To have all Norwegian translations under one key
  if (browserLocale === 'nb') browserLocale = 'no'
  const storedLocale = getStoredLocale()
  const defaultLocale = storedLocale || (browserLocale as Locale) || DEFAULT_LOCALE
  const [locale, setLocale] = useState<Locale>(defaultLocale)
  const [whiteLabel, setWhiteLabel] = useState<WhiteLabel>('skytech')
  const [isEditTranslationsMode, setIsEditTranslationsMode] = useState(false)

  const handleSetWhiteLabel = (wl: WhiteLabel) => {
    setWhiteLabel(wl)
    // todo: remove when the organisation is specified in the url
    localStorage.setItem('org', wl)
  }

  const handleSetLocale = (l: Locale) => {
    setLocale(l)
    // To preselect locale on the login page
    localStorage.setItem('locale', l)
  }

  useEffect(() => {
    getWhiteLabelFromParams({ location, setWhiteLabel: handleSetWhiteLabel })
  }, [location])

  const value: AppContext = useMemo(
    () => ({
      locale,
      whiteLabel,
      isEditTranslationsMode,
      setLocale: handleSetLocale,
      setWhiteLabel: handleSetWhiteLabel,
      setIsEditTranslationsMode,
    }),
    [isEditTranslationsMode, locale, whiteLabel]
  )

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export const useAppContext = () => useContext(Context)

function getStoredLocale() {
  const storedLocale = localStorage.getItem('locale')
  if (!storedLocale) return null

  if (isLocale(storedLocale)) return storedLocale as Locale

  return null
}
